import { useCallback, useMemo, type CSSProperties } from "react";
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  addEdge as addEdgeRf,
  applyEdgeChanges,
  applyNodeChanges,
  reconnectEdge,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeTypes,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import type {
  ProductEdge,
  ProductGraph,
  ProductNode,
  ProductNodeType,
} from "@/services/platformEnterprisesService";
import type { BranchNodeData, DecisionNodeData, GraphValidationError } from "@/lib/productGraph";

import type { BuilderNodeCase } from "./product-builder-node";
import { ProductBuilderNode } from "./product-builder-node";

const NODE_TYPES_MAP = {
  start: ProductBuilderNode,
  end: ProductBuilderNode,
  precondition: ProductBuilderNode,
  userForm: ProductBuilderNode,
  staffApproval: ProductBuilderNode,
  staffAction: ProductBuilderNode,
  branch: ProductBuilderNode,
  pricing: ProductBuilderNode,
  internalRequest: ProductBuilderNode,
  inviteActors: ProductBuilderNode,
  decision: ProductBuilderNode,
  createRequest: ProductBuilderNode,
  automation: ProductBuilderNode,
  documentCollection: ProductBuilderNode,
} as unknown as NodeTypes;

interface ProductBuilderCanvasProps {
  graph: ProductGraph;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  errors: GraphValidationError[];
  onSelectNode: (id: string | null) => void;
  onSelectEdge: (id: string | null) => void;
  onMoveNode: (
    id: string,
    position: { x: number; y: number },
    commit?: boolean,
  ) => void;
  onAddEdge: (edge: Omit<ProductEdge, "id">) => void;
  onRemoveNode: (id: string) => void;
  onRemoveEdge: (id: string) => void;
  onReconnectEdge: (
    edgeId: string,
    next: { source: string; target: string; sourceHandle?: string },
  ) => void;
  style?: CSSProperties;
}

function toRfNode(node: ProductNode, errorIds: Set<string>): Node {
  const base = {
    id: node.id,
    type: node.type,
    position: node.position,
    deletable: node.type !== "start",
    data: {
      label: (node.data as { label?: string }).label,
      description: (node.data as { description?: string }).description,
      __nodeType: node.type as ProductNodeType,
      __hasError: errorIds.has(node.id),
    } as Record<string, unknown>,
  } satisfies Node;

  if (node.type === "branch") {
    const branchData = node.data as unknown as BranchNodeData;
    const cases: BuilderNodeCase[] = (branchData.cases ?? []).map((c) => ({
      id: c.id,
      label: c.label?.trim() || `${c.op} ${String(c.value)}`,
    }));
    base.data = { ...base.data, __branchCases: cases };
  }

  if (node.type === "decision") {
    const decisionData = node.data as unknown as DecisionNodeData;
    const options: BuilderNodeCase[] = (decisionData.options ?? []).map((o) => ({
      id: o.value,
      label: o.label?.trim() || o.value,
    }));
    base.data = { ...base.data, __decisionOptions: options };
  }

  return base;
}

function toRfEdge(edge: ProductEdge, graph: ProductGraph): Edge {
  const sourceNode = graph.nodes.find((n) => n.id === edge.source);
  const handle = edge.sourceHandle;

  let label: string | undefined;
  let stroke = "#94a3b8";

  if (sourceNode?.type === "precondition") {
    if (handle === "pass") {
      label = "pass";
      stroke = "#10b981";
    } else if (handle === "fail") {
      label = "fail";
      stroke = "#f43f5e";
    }
  } else if (sourceNode?.type === "branch") {
    if (handle === "default" || !handle) {
      label = "default";
      stroke = "#64748b";
    } else {
      const branchData = sourceNode.data as unknown as BranchNodeData;
      const matchedCase = branchData.cases?.find((c) => c.id === handle);
      label =
        matchedCase?.label?.trim() ||
        (matchedCase ? `${matchedCase.op} ${String(matchedCase.value)}` : handle);
      stroke = "#f43f5e";
    }
  } else if (sourceNode?.type === "decision") {
    if (handle === "default" || !handle) {
      label = "default";
      stroke = "#64748b";
    } else {
      const decisionData = sourceNode.data as unknown as DecisionNodeData;
      const matchedOpt = decisionData.options?.find((o) => o.value === handle);
      label = matchedOpt?.label?.trim() || handle;
      stroke = "#f97316";
    }
  }

  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    label,
    style: { stroke, strokeWidth: 1.75 },
    labelBgStyle: { fill: "#fff" },
    labelStyle: { fontSize: 11, fontWeight: 500 },
    animated: false,
  };
}

function CanvasInner({
  graph,
  selectedNodeId,
  selectedEdgeId,
  errors,
  onSelectNode,
  onSelectEdge,
  onMoveNode,
  onAddEdge,
  onRemoveNode,
  onRemoveEdge,
  onReconnectEdge,
  style,
}: ProductBuilderCanvasProps): JSX.Element {
  const errorNodeIds = useMemo(() => {
    const set = new Set<string>();
    for (const err of errors) {
      if (err.nodeId) set.add(err.nodeId);
    }
    return set;
  }, [errors]);

  const nodes: Node[] = useMemo(
    () =>
      graph.nodes.map((n) => ({
        ...toRfNode(n, errorNodeIds),
        selected: n.id === selectedNodeId,
      })),
    [graph.nodes, errorNodeIds, selectedNodeId],
  );

  const edges: Edge[] = useMemo(
    () =>
      graph.edges.map((e) => ({
        ...toRfEdge(e, graph),
        selected: e.id === selectedEdgeId,
      })),
    [graph, selectedEdgeId],
  );

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const updated = applyNodeChanges(changes, nodes);
      let nextSelectedId: string | null | undefined = undefined;
      for (const change of changes) {
        if (change.type === "position" && change.position) {
          const node = updated.find((n) => n.id === change.id);
          if (node) onMoveNode(node.id, node.position, !change.dragging);
        } else if (change.type === "remove") {
          onRemoveNode(change.id);
        } else if (change.type === "select") {
          if (change.selected) {
            nextSelectedId = change.id;
          } else if (nextSelectedId === undefined) {
            nextSelectedId = null;
          }
        }
      }
      if (nextSelectedId !== undefined) onSelectNode(nextSelectedId);
    },
    [nodes, onMoveNode, onRemoveNode, onSelectNode],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      applyEdgeChanges(changes, edges);
      let nextSelectedId: string | null | undefined = undefined;
      for (const change of changes) {
        if (change.type === "remove") {
          onRemoveEdge(change.id);
        } else if (change.type === "select") {
          if (change.selected) {
            nextSelectedId = change.id;
          } else if (nextSelectedId === undefined) {
            nextSelectedId = null;
          }
        }
      }
      if (nextSelectedId !== undefined) onSelectEdge(nextSelectedId);
    },
    [edges, onRemoveEdge, onSelectEdge],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      addEdgeRf(connection, edges);
      onAddEdge({
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle ?? undefined,
      });
    },
    [edges, onAddEdge],
  );

  const handleReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      if (!newConnection.source || !newConnection.target) return;
      reconnectEdge(oldEdge, newConnection, edges);
      onReconnectEdge(oldEdge.id, {
        source: newConnection.source,
        target: newConnection.target,
        sourceHandle: newConnection.sourceHandle ?? undefined,
      });
    },
    [edges, onReconnectEdge],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={NODE_TYPES_MAP}
      onNodesChange={handleNodesChange}
      onEdgesChange={handleEdgesChange}
      onConnect={handleConnect}
      onReconnect={handleReconnect}
      onPaneClick={() => {
        onSelectNode(null);
        onSelectEdge(null);
      }}
      deleteKeyCode={["Backspace", "Delete"]}
      edgesReconnectable
      fitView
      fitViewOptions={{ padding: 0.3, maxZoom: 0.85, minZoom: 0.4 }}
      defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
      minZoom={0.2}
      maxZoom={1.5}
      style={style}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={16} />
      <Controls position="bottom-right" />
    </ReactFlow>
  );
}

export function ProductBuilderCanvas(props: ProductBuilderCanvasProps): JSX.Element {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}
